import { useState, useEffect } from'react';
import { motion, AnimatePresence } from'framer-motion';

interface MerkavahChariotProps {
 className?: string;
}

export function MerkavahChariot({ className ='' }: MerkavahChariotProps) {
 const [activeLayer, setActiveLayer] = useState<number | null>(null);
 const [rotation, setRotation] = useState(0);
 const [isAnimating, setIsAnimating] = useState(false);

 // Auto-rotate wheels
 useEffect(() => {
 if (isAnimating) {
 const interval = setInterval(() => {
 setRotation((prev) => (prev + 2) % 360);
 }, 50);
 return () => clearInterval(interval);
 }
 }, [isAnimating]);

 const layers = [
 {
 id: 0,
 name:'Divine Throne',
 hebrew:'כסא הכבוד',
 color:'#7C3AED',
 position:'top-0',
 description:'The sapphire throne of glory, seat of the divine presence',
 details:'Ezekiel describes"the likeness of a throne, in appearance like sapphire; and seated above... a likeness with a human appearance"',
 },
 {
 id: 1,
 name:'Firmament',
 hebrew:'רקיע',
 color:'#06B6D4',
 position:'top-16',
 description:'The crystal expanse above the living creatures',
 details:'A firmament"like the color of the terrible crystal" spreads over the heads of the creatures',
 },
 {
 id: 2,
 name:'Chayot (Living Creatures)',
 hebrew:'חיות הקדש',
 color:'#EA580C',
 position:'top-32',
 description:'Four living beings bearing the throne',
 details:'Each with four faces (human, lion, ox, eagle) and four wings, moving as one',
 },
 {
 id: 3,
 name:'Ophanim (Wheels)',
 hebrew:'אופנים',
 color:'#FBBF24',
 position:'top-48',
 description:'Wheels within wheels, covered with eyes',
 details:'Wheels intersecting at right angles, their rims full of eyes all around',
 },
 ];

 return (
 <div className={`relative ${className}`}>
 {/* Control Panel */}
 <div className="mb-6 flex items-center justify-center gap-4">
 <button
 onClick={() => setIsAnimating(!isAnimating)}
 className="px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg font-medium transition-all duration-200"
 >
 {isAnimating ?'Pause Rotation' :'Animate Wheels'}
 </button>
 <button
 onClick={() => setActiveLayer(null)}
 className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-all duration-200"
 >
 Reset View
 </button>
 </div>

 {/* Main Chariot Visualization */}
 <div className="relative w-full max-w-2xl mx-auto aspect-square bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl border border-slate-700 overflow-hidden">
 {/* Background stars/divine radiance */}
 <div className="absolute inset-0 opacity-20">
 {[...Array(50)].map((_, i) => (
 <motion.div
 key={i}
 className="absolute w-1 h-1 bg-white rounded-full"
 style={{
 left: `${Math.random() * 100}%`,
 top: `${Math.random() * 100}%`,
 }}
 animate={{
 opacity: [0.2, 1, 0.2],
 scale: [1, 1.5, 1],
 }}
 transition={{
 duration: 2 + Math.random() * 2,
 repeat: Infinity,
 delay: Math.random() * 2,
 }}
 />
 ))}
 </div>

 {/* Chariot Layers */}
 <div className="relative w-full h-full flex flex-col items-center justify-center p-8">
 {/* Divine Throne (top) */}
 <motion.button
 onClick={() => setActiveLayer(0)}
 className={`relative z-40 mb-4 transition-all duration-300 ${
 activeLayer === 0 ?'scale-110' :'hover:scale-105'
 }`}
 whileHover={{ scale: 1.05 }}
 >
 <div
 className="w-32 h-32 rounded-lg flex items-center justify-center"
 style={{
 background: `linear-gradient(135deg, ${layers[0].color}40, ${layers[0].color}80)`,
 border: `2px solid ${activeLayer === 0 ? layers[0].color :'transparent'}`,
 boxShadow: activeLayer === 0 ? `0 0 30px ${layers[0].color}80` :'none',
 }}
 >
 <div className="text-center">
 <div className="text-4xl mb-2">👑</div>
 <div className="hebrew-text text-sm" style={{ color: layers[0].color }}>
 {layers[0].hebrew}
 </div>
 </div>
 </div>
 </motion.button>

 {/* Firmament */}
 <motion.button
 onClick={() => setActiveLayer(1)}
 className={`relative z-30 mb-4 transition-all duration-300 ${
 activeLayer === 1 ?'scale-110' :'hover:scale-105'
 }`}
 whileHover={{ scale: 1.05 }}
 >
 <div
 className="w-40 h-8 rounded-full flex items-center justify-center"
 style={{
 background: `linear-gradient(90deg, ${layers[1].color}20, ${layers[1].color}60, ${layers[1].color}20)`,
 border: `2px solid ${activeLayer === 1 ? layers[1].color :'transparent'}`,
 boxShadow: activeLayer === 1 ? `0 0 30px ${layers[1].color}80` :'none',
 }}
 >
 <div className="hebrew-text text-xs" style={{ color: layers[1].color }}>
 {layers[1].hebrew}
 </div>
 </div>
 </motion.button>

 {/* Chayot (Living Creatures) */}
 <motion.button
 onClick={() => setActiveLayer(2)}
 className={`relative z-20 mb-4 transition-all duration-300 ${
 activeLayer === 2 ?'scale-110' :'hover:scale-105'
 }`}
 whileHover={{ scale: 1.05 }}
 >
 <div className="relative">
 {/* Four creatures in cardinal directions */}
 <div className="relative w-48 h-48">
 {['🦁','🐂','🦅','👤'].map((emoji, i) => {
 const angle = (i * 90 - 45) * (Math.PI / 180);
 const radius = 60;
 const x = radius * Math.cos(angle);
 const y = radius * Math.sin(angle);
 return (
 <motion.div
 key={i}
 className="absolute w-12 h-12 flex items-center justify-center text-2xl"
 style={{
 left:'50%',
 top:'50%',
 marginLeft: `${x - 24}px`,
 marginTop: `${y - 24}px`,
 background: `${layers[2].color}40`,
 borderRadius:'50%',
 border: `2px solid ${activeLayer === 2 ? layers[2].color :'transparent'}`,
 }}
 animate={{
 rotate: isAnimating ? [0, 360] : 0,
 }}
 transition={{
 duration: 8,
 repeat: Infinity,
 ease:'linear',
 }}
 >
 {emoji}
 </motion.div>
 );
 })}
 {/* Center */}
 <div
 className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full flex items-center justify-center"
 style={{
 background: `${layers[2].color}60`,
 border: `2px solid ${layers[2].color}`,
 boxShadow: activeLayer === 2 ? `0 0 30px ${layers[2].color}80` :'none',
 }}
 >
 <div className="hebrew-text text-xs" style={{ color: layers[2].color }}>
 {layers[2].hebrew}
 </div>
 </div>
 </div>
 </div>
 </motion.button>

 {/* Ophanim (Wheels) */}
 <motion.button
 onClick={() => setActiveLayer(3)}
 className={`relative z-10 transition-all duration-300 ${
 activeLayer === 3 ?'scale-110' :'hover:scale-105'
 }`}
 whileHover={{ scale: 1.05 }}
 >
 <div className="relative w-56 h-56">
 {/* Outer wheel */}
 <motion.div
 className="absolute inset-0 rounded-full flex items-center justify-center"
 style={{
 background: `conic-gradient(from 0deg, ${layers[3].color}00, ${layers[3].color}60, ${layers[3].color}00)`,
 border: `3px solid ${activeLayer === 3 ? layers[3].color : `${layers[3].color}60`}`,
 boxShadow: activeLayer === 3 ? `0 0 40px ${layers[3].color}80` :'none',
 }}
 animate={{
 rotate: rotation,
 }}
 >
 {/* Eyes around the rim */}
 {[...Array(12)].map((_, i) => {
 const angle = (i * 30) * (Math.PI / 180);
 const radius = 100;
 const x = radius * Math.cos(angle);
 const y = radius * Math.sin(angle);
 return (
 <motion.div
 key={i}
 className="absolute text-xs"
 style={{
 left:'50%',
 top:'50%',
 marginLeft: `${x - 8}px`,
 marginTop: `${y - 8}px`,
 }}
 animate={{
 scale: isAnimating ? [1, 1.3, 1] : 1,
 }}
 transition={{
 duration: 1.5,
 repeat: Infinity,
 delay: i * 0.1,
 }}
 >
 👁️
 </motion.div>
 );
 })}
 </motion.div>

 {/* Inner wheel (perpendicular) */}
 <motion.div
 className="absolute inset-4 rounded-full"
 style={{
 background: `conic-gradient(from 90deg, ${layers[3].color}00, ${layers[3].color}40, ${layers[3].color}00)`,
 border: `2px solid ${layers[3].color}40`,
 }}
 animate={{
 rotate: -rotation,
 }}
 />

 {/* Center label */}
 <div
 className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full flex items-center justify-center"
 style={{
 background: `${layers[3].color}80`,
 border: `2px solid ${layers[3].color}`,
 }}
 >
 <div className="hebrew-text text-xs text-center" style={{ color:'#000' }}>
 {layers[3].hebrew}
 </div>
 </div>
 </div>
 </motion.button>
 </div>
 </div>

 {/* Layer Info Panel */}
 <AnimatePresence>
 {activeLayer !== null && (
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 exit={{ opacity: 0, y: -20 }}
 className="mt-6 p-6 rounded-xl border"
 style={{
 backgroundColor: `${layers[activeLayer].color}10`,
 borderColor: `${layers[activeLayer].color}40`,
 }}
 >
 <div className="flex items-center justify-between mb-4">
 <div>
 <h3 className="text-xl font-bold text-white">{layers[activeLayer].name}</h3>
 <p
 className="hebrew-text text-lg"
 style={{ color: layers[activeLayer].color }}
 >
 {layers[activeLayer].hebrew}
 </p>
 </div>
 <button
 onClick={() => setActiveLayer(null)}
 className="text-slate-400 hover:text-white text-2xl"
 >
 ✕
 </button>
 </div>
 <p className="text-slate-300 mb-3">{layers[activeLayer].description}</p>
 <p className="text-sm text-slate-400 italic border-l-2 pl-3" style={{ borderColor: layers[activeLayer].color }}>
 {layers[activeLayer].details}
 </p>
 </motion.div>
 )}
 </AnimatePresence>

 {/* Legend */}
 <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-4 gap-3">
 {layers.map((layer) => (
 <button
 key={layer.id}
 onClick={() => setActiveLayer(layer.id)}
 className={`p-3 rounded-lg border transition-all duration-200 ${
 activeLayer === layer.id
 ?'border-2'
 :'border hover:border-2'
 }`}
 style={{
 backgroundColor: `${layer.color}10`,
 borderColor: activeLayer === layer.id ? layer.color : `${layer.color}40`,
 }}
 >
 <div className="text-xs font-semibold text-white mb-1">{layer.name}</div>
 <div className="hebrew-text text-[10px]" style={{ color: layer.color }}>
 {layer.hebrew}
 </div>
 </button>
 ))}
 </div>
 </div>
 );
}
